// Modelo de datos y defaults del Editor de Actas de Asamblea (Habitia).
// Port del modelo en design_handoff_editor_actas/spec/types.ts a estructuras JS planas,
// más helpers para construir el padrón (edificio + unidades + copropietarios) desde
// las colecciones Firestore `apartments` / `users`.
//
// El acta es una entidad propia que referencia unidades y personas por id; la fuente
// de verdad del padrón y las alícuotas es la plataforma (no se duplica).

// ───────────────────────── IDs locales ─────────────────────────
// IDs efímeros para puntos del orden del día (no son de Firestore).
let _seq = 0;
export function uid(prefix = "p") {
  _seq += 1;
  return `${prefix}_${Date.now().toString(36)}_${_seq}`;
}

// ── Votante externo (apoderado que no es copropietario del edificio) ──
// Se guarda como votanteId con este prefijo + un slug del nombre, y el nombre
// legible en votanteNombre. El motor de votación agrupa por votanteId, así que
// dos unidades con el mismo apoderado externo (mismo nombre) se computan juntas.
export const EXT_VOTANTE_PREFIX = "ext:";

export function esVotanteExterno(votanteId) {
  return String(votanteId || "").startsWith(EXT_VOTANTE_PREFIX);
}

export function externoIdDesdeNombre(nombre, fallbackKey = "") {
  const slug = String(nombre || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return EXT_VOTANTE_PREFIX + (slug || fallbackKey || "sin-nombre");
}

/** Nombre legible del votante: copropietario del padrón o apoderado externo. */
export function nombreVotante(votanteId, asistencia, coproById) {
  const c = coproById?.get?.(String(votanteId));
  if (c) return c.nombre;
  const row = (asistencia || []).find(
    (a) => String(a.votanteId) === String(votanteId) && a.votanteNombre
  );
  if (row?.votanteNombre) return row.votanteNombre;
  return String(votanteId || "").replace(/^ext:/, "").replace(/-/g, " ").trim() || "—";
}

export const TIPOS_ASAMBLEA = [
  { value: "ordinaria", label: "Ordinaria" },
  { value: "extraordinaria", label: "Extraordinaria" },
];

export const CONVOCATORIAS = [
  { value: "primera", label: "Primera" },
  { value: "segunda", label: "Segunda" },
];

export const TIPOS_RESOLUCION = [
  { value: "mayoria", label: "Mayoría" },
  { value: "unanime", label: "Unánime" },
  { value: "informativa", label: "Informativa (sin votación)" },
];

export const METODOS_VOTACION = [
  { value: "unidades", label: "Por unidades" },
  { value: "coeficiente", label: "Por coeficiente" },
];

export const SENTIDOS = [
  { key: "favor", label: "A favor", color: "#0A8F4F" },
  { key: "contra", label: "En contra", color: "#C24545" },
  { key: "abstencion", label: "Abstención", color: "#9AA0AB" },
];

/** Votación vacía para un punto del orden del día. */
export function emptyVotacion() {
  return {
    metodo: "unidades",
    votosPorPersona: { favor: [], contra: [], abstencion: [] },
  };
}

/** Punto del orden del día por defecto. */
export function emptyPunto(numero = 1) {
  return {
    id: uid("pt"),
    numero,
    seccion: "",
    titulo: "",
    cuerpo: "",
    tipoResolucion: "mayoria",
    textoResolucion: "",
    votacion: emptyVotacion(),
  };
}

/**
 * Acta por defecto. Recibe opcionalmente el padrón (edificio) para precargar
 * la tabla de asistencia (una fila por unidad, presente=false, votante=titular).
 */
export function defaultActa(edificio = null) {
  const asistencia = (edificio?.unidades || []).map((u) => ({
    unidadId: u.id,
    presente: false,
    votanteId: u.titularId || "",
    votanteNombre: "", // solo se usa si el votante es un apoderado externo al edificio
    nota: "",
  }));
  return {
    id: null, // id de Firestore una vez guardada
    edificioId: edificio?.id || "",
    edificioNombre: edificio?.nombre || "",
    padron: edificio?.padron || "",
    tipo: "ordinaria",
    convocatoria: "segunda",
    fecha: "", // ISO "YYYY-MM-DD"
    ciudad: "Montevideo",
    horaApertura: "",
    horaCierre: "",
    folio: "",
    libro: "I",
    actaNumero: "",
    // Texto editorial de portada / apertura / cierre (mantiene la sensación de documento)
    epigrafe: "Asamblea de Copropietarios",
    tituloPortada: "Acta de Asamblea Ordinaria",
    leadPortada:
      "Resoluciones adoptadas por los copropietarios en sesión convocada conforme a la Ley N° 10.751 de Propiedad Horizontal y al Reglamento de Copropiedad del edificio.",
    textoApertura: "",
    textoCierre:
      "Habiéndose agotado los puntos del orden del día, se da por concluida la sesión. La presente acta queda asentada en el Libro de Actas del Edificio y disponible en formato digital a través de Habitia.",
    administracion: "Habitia",
    administracionDetalle: "",
    // Asistencia + orden del día
    asistencia,
    ordenDelDia: [emptyPunto(1)],
    // Firmas
    presidenciaId: "",
    presidenciaNombre: "",
    presidenciaSub: "",
    secretariaId: "",
    secretariaNombre: "",
    secretariaSub: "",
  };
}

/**
 * Construye el padrón (edificio + unidades + copropietarios) a partir de los
 * documentos `apartments` de un edificio y un mapa de usuarios por userid.
 *
 * @param {object} building   - doc de building seleccionado ({ buildingid, name, rut, ... })
 * @param {Array}  apartments - docs de apartments ({ id, number, owner, ownerSecondary, alicuota, m2, ... })
 * @param {Map<string,object>} usersById - userid(string) -> { name, email, ... }
 * @returns {{ edificio: object, copropietarios: object[] }}
 */
export function buildPadron(building, apartments, usersById) {
  const coproMap = new Map();
  const addCopro = (rawId) => {
    if (rawId == null || rawId === "") return "";
    const id = String(rawId);
    if (!coproMap.has(id)) {
      const u = usersById.get(id);
      coproMap.set(id, {
        id,
        nombre: u?.name || `ID ${id}`,
        ci: u?.ci || u?.documento || "",
        email: u?.email || "",
      });
    }
    return id;
  };

  const sorted = [...(apartments || [])].sort(
    (a, b) => (Number(a.floor) || 0) - (Number(b.floor) || 0) ||
      String(a.number ?? "").localeCompare(String(b.number ?? ""), "es", { numeric: true })
  );

  const unidades = sorted.map((apt) => {
    const titularId = addCopro(apt.owner);
    if (apt.ownerSecondary) addCopro(apt.ownerSecondary);
    const alicuota =
      apt.alicuota != null && apt.alicuota !== "" && !Number.isNaN(Number(apt.alicuota))
        ? Number(apt.alicuota)
        : undefined;
    return {
      id: String(apt.id),
      etiqueta: String(apt.number ?? apt.id),
      alicuota,
      titularId,
      m2: apt.m2 || "",
    };
  });

  return {
    edificio: {
      id: String(building?.buildingid ?? ""),
      nombre: building?.name || "",
      padron: building?.padron || building?.rut || "",
      unidades,
    },
    copropietarios: [...coproMap.values()],
  };
}

/**
 * Reconcilia la asistencia de un acta con el padrón actual: conserva filas cuyas
 * unidades siguen existiendo y agrega filas nuevas (presente=false). Útil al cargar
 * un acta guardada o al refrescar el padrón.
 */
export function reconciliarAsistencia(asistencia, edificio) {
  const prev = new Map((asistencia || []).map((a) => [String(a.unidadId), a]));
  return (edificio?.unidades || []).map((u) => {
    const p = prev.get(String(u.id));
    return p
      ? {
          unidadId: u.id,
          presente: !!p.presente,
          votanteId: p.votanteId ?? u.titularId ?? "",
          votanteNombre: p.votanteNombre || "",
          nota: p.nota || "",
        }
      : { unidadId: u.id, presente: false, votanteId: u.titularId || "", votanteNombre: "", nota: "" };
  });
}

// ───────────────────────── Formateo ─────────────────────────

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/** "2026-05-06" -> "06 / 05 / 2026" (vacío si no hay fecha). */
export function formatFechaSlash(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return iso;
  return `${d} / ${m} / ${y}`;
}

/** "2026-05-06" -> "seis días del mes de mayo del año dos mil veintiséis" (aprox). */
export function formatFechaLarga(iso) {
  if (!iso) return "____";
  const [y, m, d] = String(iso).split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return iso;
  const mes = MESES[m - 1] || "";
  return `${d} de ${mes} de ${y}`;
}

const NUM_PAD = (n) => String(n).padStart(2, "0");

/** "01", "02", ... para tags de sección/puntos. */
export function pad2(n) {
  return NUM_PAD(n);
}

/** Limpia el acta para persistir en Firestore (sin objetos no serializables). */
export function serializeActa(acta) {
  return JSON.parse(JSON.stringify(acta));
}
